"use client";
import { useEffect, useState, useRef } from "react";
import classNames from "classnames";

export interface SearchInputProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: (e: { value: string; _e: React.ChangeEvent<HTMLInputElement> }) => void;
  onDebouncedChange?: (value: string) => void;
  debounceWaitTime?: number;
  placeholder?: string;
  className?: string; // class for the input itself
  containerClass?: string; // class for the container
}

const SearchInput = ({
  id,
  name,
  value: controlledValue,
  onChange,
  onDebouncedChange,
  debounceWaitTime = 400,
  placeholder = "SEARCH",
  className,
  containerClass,
}: SearchInputProps) => {
  const [internalValue, setInternalValue] = useState(controlledValue || "");
  const isFirstRender = useRef(true);

  // Sync internal value with controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  // Handle debounced change
  useEffect(() => {
    // Skip the first render to avoid firing onDebouncedChange with initial value unless it's explicitly needed
    // However, if controlledValue changes, we might want it.
    // But usually onDebouncedChange is for user input.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const handler = setTimeout(() => {
      if (onDebouncedChange) {
        onDebouncedChange(internalValue.trim());
      }
    }, debounceWaitTime);

    return () => clearTimeout(handler);
  }, [internalValue, onDebouncedChange, debounceWaitTime]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);

    if (onChange) {
      onChange({ value: newValue, _e: e });
    }
  };

  return (
    <div className={classNames(containerClass)}>
      <input
        id={id}
        name={name}
        type="search"
        value={internalValue}
        onChange={handleChange}
        className={classNames(className)}
        placeholder={placeholder}
      />
    </div>
  );
};

export default SearchInput;
